<template>
  <v-form ref="form" v-model="valid" v-if="currentUser">
    <v-card>
      <v-card-title>
        {{ $t("bookingForm.title", { year: new Date().getFullYear() }) }}
      </v-card-title>

      <v-card-text>
        {{ $t("bookingForm.description") }}
      </v-card-text>

      <v-simple-table class="table-no-hover">
        <template #default>
          <tbody>
            <template v-for="(fund, index) in formData.funds">
              <tr :key="`fund-${index}`">
                <td width="50%">
                  <v-text-field
                    v-model="formData.funds[index].name"
                    :label="$t('bookingForm.fund.name.label')"
                    type="text"
                    :rules="[(v) => !!v || $t('validation.required')]"
                    required
                  ></v-text-field>
                </td>

                <td>
                  <v-select
                    v-model="formData.funds[index].type"
                    :items="[
                      { text: $t('bookingForm.fund.precheck.label'), value: 'PRECHECK' },
                      { text: $t('bookingForm.fund.minimal.label'), value: 'MINIMAL' },
                      { text: $t('bookingForm.fund.stepModel.label'), value: 'STEP_MODEL' },
                    ]"
                    :label="$t('bookingForm.fund.type.label')"
                    :rules="[(v) => !!v || $t('validation.required')]"
                    required
                  ></v-select>
                </td>

                <td width="1%">
                  <v-btn color="warning" @click="remove(index)" small>
                    {{ $t("bookingForm.removeFund") }}
                  </v-btn>
                </td>
              </tr>

              <tr :key="`yellow-precheck-${index}`" v-if="formData.funds[index].precheck && showPrecheckYellowInfo">
                <td colspan="4">
                  <v-alert color="yellow" v-if="formData.funds[index].precheck">
                    {{ $t("bookingForm.precheckYellowInfo") }}
                  </v-alert>
                </td>
              </tr>

              <tr :key="`orange-precheck-${index}`" v-if="formData.funds[index].precheck && showPrecheckOrangeInfo">
                <td colspan="4">
                  <v-alert color="orange" dark v-if="formData.funds[index].precheck">
                    {{ $t("bookingForm.precheckOrangeInfo") }}
                  </v-alert>
                </td>
              </tr>

              <tr :key="`red-precheck-${index}`" v-if="formData.funds[index].precheck && showPrecheckRedInfo">
                <td colspan="4">
                  <v-alert type="error" v-if="formData.funds[index].precheck">
                    {{ $t("bookingForm.precheckRedInfo") }}
                  </v-alert>
                </td>
              </tr>

              <tr :key="`yellow-${index}`" v-if="!formData.funds[index].precheck && showYellowInfo">
                <td colspan="4">
                  <v-alert color="yellow" v-if="!formData.funds[index].precheck">
                    {{ $t("bookingForm.yellowInfo") }}
                  </v-alert>
                </td>
              </tr>

              <tr :key="`orange-${index}`" v-if="!formData.funds[index].precheck && showOrangeInfo">
                <td colspan="4">
                  <v-alert color="orange" dark v-if="!formData.funds[index].precheck">
                    {{ $t("bookingForm.orangeInfo") }}
                  </v-alert>
                </td>
              </tr>

              <tr :key="`red-${index}`" v-if="!formData.funds[index].precheck && showRedInfo">
                <td colspan="4">
                  <v-alert type="error" v-if="!formData.funds[index].precheck">
                    {{ $t("bookingForm.redInfo") }}
                  </v-alert>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-btn color="secondary" @click="add" small class="mb-8">
              {{ $t("bookingForm.addFund") }}
            </v-btn>
          </v-col>

          <v-col cols="12">
            <div class="text-subtitle-2">{{ $t("bookingForm.legal.title") }}</div>

            <div v-html="$t('bookingForm.legal.links')" class="mt-2"></div>

            <v-checkbox
              v-model="acceptedConditions"
              :label="$t('bookingForm.legal.acceptConditions')"
              :rules="[(v) => !!v || $t('validation.required')]"
            ></v-checkbox>

            <v-checkbox
              v-model="confirmedAuthorization"
              :label="$t('bookingForm.legal.confirmAuthorization')"
              :rules="[(v) => !!v || $t('validation.required')]"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-dialog v-model="dialog" persistent max-width="600">
          <template #activator="{ on, attrs }">
            <v-btn :disabled="!valid || formData.funds.length === 0" color="primary" v-bind="attrs" v-on="on">
              {{ $t("bookingForm.save") }}
            </v-btn>
          </template>

          <v-card>
            <v-card-title class="headline">
              {{ $t("bookingForm.confirmation.title") }}
            </v-card-title>

            <v-card-text>
              <Markdown :value="$t('bookingForm.confirmation.description')"></Markdown>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn color="warning" @click="dialog = false">
                {{ $t("bookingForm.confirmation.cancel") }}
              </v-btn>

              <v-btn color="primary" @click="save" :loading="loading">
                {{ $t("bookingForm.confirmation.accept") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { remove, map } from "ramda";
import CreateBookingMutation from "@/gql/mutations/CreateBookingMutation.gql";
import BookingsQuery from "@/gql/queries/BookingsQuery.gql";
import OptionsQuery from "@/gql/queries/auditor/OptionsQuery.gql";

export default {
  data() {
    return {
      formData: {
        funds: [],
      },
      acceptedConditions: false,
      confirmedAuthorization: false,
      valid: false,
      loading: false,
      dialog: false,
      options: [],
      currentDate: new Date(),
    };
  },

  computed: {
    yellowDeadline() {
      return new Date(this.options.find(({ key }) => key === "yellowDeadline")?.value);
    },

    orangeDeadline() {
      return new Date(this.options.find(({ key }) => key === "orangeDeadline")?.value);
    },

    redDeadline() {
      return new Date(this.options.find(({ key }) => key === "redDeadline")?.value);
    },

    precheckYellowDeadline() {
      return new Date(this.options.find(({ key }) => key === "precheckYellowDeadline")?.value);
    },

    precheckOrangeDeadline() {
      return new Date(this.options.find(({ key }) => key === "precheckOrangeDeadline")?.value);
    },

    precheckRedDeadline() {
      return new Date(this.options.find(({ key }) => key === "precheckRedDeadline")?.value);
    },

    showRedInfo() {
      return this.currentDate > this.redDeadline;
    },

    showOrangeInfo() {
      return !this.showRedInfo && this.currentDate > this.orangeDeadline;
    },

    showYellowInfo() {
      return !this.showRedInfo && !this.showOrangeInfo && this.currentDate > this.yellowDeadline;
    },

    showPrecheckRedInfo() {
      return this.currentDate > this.precheckRedDeadline;
    },

    showPrecheckOrangeInfo() {
      return !this.showPrecheckRedInfo && this.currentDate > this.precheckOrangeDeadline;
    },

    showPrecheckYellowInfo() {
      return (
        !this.showPrecheckRedInfo && !this.showPrecheckOrangeInfo && this.currentDate > this.precheckYellowDeadline
      );
    },
  },

  apollo: {
    currentUser: {
      query: BookingsQuery,
      fetchPolicy: "cache-and-network",
    },
    options: {
      query: OptionsQuery,
      fetchPolicy: "cache-and-network",
    },
  },

  methods: {
    add() {
      this.formData.funds.push({
        name: "",
        type: "MINIMAL",
      });
    },

    remove(index) {
      this.formData.funds = remove(index, 1, this.formData.funds);
    },

    async save() {
      this.loading = true;

      const funds = map(
        ({ name, type }) => ({
          name,
          type,
        }),
        this.formData.funds
      );

      const input = {
        ...this.formData,
        funds,
        acceptedConditions: this.acceptedConditions,
        confirmedAuthorization: this.confirmedAuthorization,
      };

      await this.$apollo.mutate({
        mutation: CreateBookingMutation,
        variables: {
          input,
        },
      });

      this.$refs.form.reset();
      this.formData.funds = [];

      this.dialog = false;
      this.loading = false;
    },
  },
};
</script>
